"use client";

import { useEffect, useState } from "react";

import Badge from "@/components/common/Badge/Badge";
import { readQuizResurfaceProduct } from "@/components/common/QuizResurfaceBanner/quizResurfaceProduct";
import Picture from "@/components/ui-kit/Picture/Picture";
import { cn } from "@/utils/general";

import styles from "./HomepageHeroImage.module.scss";
import {
  HomepageHeroImageData,
  HomepageHeroImageLower,
  HomepageHeroImageProps,
} from "./HomepageHeroImage.types";
import HomepageHeroImageLowerSection from "./HomepageHeroImageLowerSection";
import HomepageHeroImageTitle from "./HomepageHeroImageTitle";

const hasLowerSection = (
  data: HomepageHeroImageData
): data is HomepageHeroImageData & HomepageHeroImageLower =>
  Boolean(data.pill && data.benefits && data.tabs && data.buttons);

const HomepageHeroImage = ({ data, isNarrow = true, theme }: HomepageHeroImageProps) => {
  const { top } = data;
  const isMen = theme === "men";
  const isCentered = isMen || top.tabletCentered;

  /*
   * Cookie is client-only. Reading it in the first render would mismatch the server HTML
   * now that this hero is server-rendered.
   */
  const [hasResurface, setHasResurface] = useState(false);

  useEffect(() => {
    setHasResurface(Boolean(readQuizResurfaceProduct()));
  }, []);
  const resurfaceArt = hasResurface ? top.background.resurface : undefined;

  return (
    <div>
      <section
        className={cn(
          styles.topHero,
          top.tabletCentered && styles.topHeroCentered,
          isMen && styles.topHeroMen
        )}
      >
        <Picture
          src={top.background.mobile}
          sources={[
            {
              media: "(min-width: 1024px)",
              srcSet: resurfaceArt ?? top.background.desktop,
            },
            { media: "(min-width: 768px)", srcSet: top.background.tablet },
          ]}
          alt=""
          role="presentation"
          loading="eager"
          fetchPriority="high"
          pictureClassName={styles.topBackground}
        />
        <div
          className={cn(
            styles.topScrim,
            top.tabletCentered && styles.topScrimCentered,
            isMen && styles.topScrimMen
          )}
        />
        {isMen ? (
          <div className="absolute inset-x-0 top-10 z-[1] md:top-[29px] lg:top-[168px]">
            <Badge theme="men" mode="dark" />
          </div>
        ) : top.tabletCentered ? (
          <div className="absolute inset-x-0 top-5 z-[1] lg:hidden">
            <Badge
              classes="mx-auto flex flex-col text-center"
              mode="dark"
              lineOneProps={{ className: "Body-20-Dia-Strong mb-[2px] text-nova-base-white" }}
              lineTwoProps={{ className: "Body-20-Dia-Normal text-nova-base-white" }}
            />
          </div>
        ) : null}
        <div
          className={cn(
            styles.topContent,
            isCentered && styles.topContentCentered,
            isMen && styles.topContentMen
          )}
        >
          <HomepageHeroImageTitle
            heading={top.heading}
            subheading={top.subheading}
            buttons={top.buttons}
            tabletCentered={isCentered}
            theme={theme}
          />
        </div>
      </section>
      {hasLowerSection(data) ? (
        <HomepageHeroImageLowerSection data={data} isNarrow={isNarrow} />
      ) : null}
    </div>
  );
};

export default HomepageHeroImage;
